/* ============================================================================
 * cart-drawer.js — Decoplantería
 * Cart drawer de Dawn + barra de regalo por tramos + kill switch remoto.
 *
 * KILL SWITCH: se controla editando el Gist. No requiere admin.
 *   { "rewardBarEnabled": false }  -> barra oculta + regalos removidos
 *   { "rewardBarEnabled": true  }  -> operación normal
 *
 * El estado NO se escribe en el div de la barra (Shopify lo re-renderiza y se
 * pierde). Se escribe como clase en <html>, que nunca es re-renderizado, y una
 * regla CSS lo aplica a cualquier nodo que aparezca después.
 * ==========================================================================*/

/* ------------------------------------------------------------------------- *
 * 1. KILL SWITCH — se ejecuta de inmediato, antes que todo lo demás
 * ------------------------------------------------------------------------- */
const RewardBarSwitch = {
  CONFIG_URL:
    'https://gist.githubusercontent.com/digitalizamecl/2d7d717adb0ec031da02e07107b3ce8b/raw/decoplanteria-config.json',
  STORAGE_KEY: 'dp_rewardBarEnabled',
  OFF_CLASS: 'reward-bar-off',
  TIMEOUT_MS: 4000,
  REVALIDATE_MS: 5 * 60 * 1000, // re-chequeo con la pestaña abierta (0 = off)

  enabled: true,
  _settled: false,
  _cached: null,

  /* Inyecta la regla CSS. Vive en <head>, fuera de todo lo que Shopify
     re-renderiza, así que sobrevive a renderContents() y refreshDrawer(). */
  injectStyles() {
    if (document.getElementById('reward-bar-killswitch-style')) return;
    const style = document.createElement('style');
    style.id = 'reward-bar-killswitch-style';
    style.textContent =
      'html.' + this.OFF_CLASS + ' #CartDrawer-RewardBar,' +
      'html.' + this.OFF_CLASS + ' .cart-reward-bar {' +
      'display:none !important;}';
    (document.head || document.documentElement).appendChild(style);
  },

  readCache() {
    try {
      const v = localStorage.getItem(this.STORAGE_KEY);
      if (v === '0') return false;
      if (v === '1') return true;
    } catch (e) {}
    return null; // sin decisión previa
  },

  writeCache(enabled) {
    try { localStorage.setItem(this.STORAGE_KEY, enabled ? '1' : '0'); } catch (e) {}
  },

  apply(enabled) {
    this.enabled = enabled;
    document.documentElement.classList.toggle(this.OFF_CLASS, !enabled);
    document.documentElement.setAttribute('data-reward-bar', enabled ? 'on' : 'off');
  },

  async fetchRemote() {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), this.TIMEOUT_MS);
    try {
      const resp = await fetch(this.CONFIG_URL + '?t=' + Date.now(), {
        cache: 'no-store',
        signal: ctrl.signal,
      });
      if (!resp.ok) throw new Error('HTTP ' + resp.status);
      const cfg = await resp.json();
      // Sólo el booleano explícito `false` apaga.
      return cfg.rewardBarEnabled !== false;
    } finally {
      clearTimeout(timer);
    }
  },

  async resolve() {
    try {
      const enabled = await this.fetchRemote();
      this.writeCache(enabled);
      return enabled;
    } catch (e) {
      // Gist caído / timeout / sin red: se conserva la ÚLTIMA DECISIÓN CONOCIDA.
      // Nunca volver a `true` a ciegas, o apagar la barra y que falle el Gist
      // la resucita en producción.
      console.warn('[RewardBarSwitch] sin respuesta del config, usando caché:', e.message);
      return this._cached === null ? true : this._cached;
    }
  },

  /* Estado optimista inmediato con el valor de la visita anterior. El drawer
     está cerrado al cargar la página, así que el usuario nunca ve un flash. */
  boot() {
    this.injectStyles();
    this._cached = this.readCache();
    this.apply(this._cached === null ? true : this._cached);
  },

  async init() {
    const enabled = await this.resolve();
    this.apply(enabled);
    this._settled = true;

    if (!enabled) {
      await CartRewardBar.removeAllGifts();
      return;
    }

    CartRewardBar.sync();

    setInterval(() => {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && drawer.classList.contains('active')) CartRewardBar.sync();
    }, 2000);

    if (this.REVALIDATE_MS > 0) {
      setInterval(async () => {
        const next = await this.resolve();
        if (next !== this.enabled) {
          this.apply(next);
          if (!next) await CartRewardBar.removeAllGifts();
        }
      }, this.REVALIDATE_MS);
    }
  },
};

RewardBarSwitch.boot();

/* ------------------------------------------------------------------------- *
 * 2. CART DRAWER (Dawn)
 * ------------------------------------------------------------------------- */
class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
      // La barra ya viene oculta por CSS si el switch está apagado: el div
      // renace en cada re-render pero la regla sobre <html> lo alcanza igual.
      if (RewardBarSwitch.enabled) {
        CartRewardBar.sync();
      } else if (RewardBarSwitch._settled) {
        CartRewardBar.removeAllGifts();
      }
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

/* ------------------------------------------------------------------------- *
 * 3. CART REWARD BAR — Decoplantería
 *    2 tramos, productos distintos, excluyentes (reemplaza)
 * ------------------------------------------------------------------------- */
const CartRewardBar = {

  TIERS: [
    {
      threshold: 7999000,        // $79.990 en centavos Shopify
      variantId: 47057279385785, // Suculenta Sorpresa
      quantity: 1,
      label: 'suculenta sorpresa',
    },
    {
      threshold: 12000000,       // $120.000 en centavos Shopify
      variantId: 45490633965753, // Sucu Box
      quantity: 1,
      label: 'sucu box',
    },
  ],

  _processing: false,
  _purging: false,

  formatMoney(cents) {
    return '$' + (cents / 100).toLocaleString('es-CL', { maximumFractionDigits: 0 });
  },

  isGiftItem(item) {
    // Solo detectar por propiedad _gift para no interferir con
    // compras normales de los mismos productos (suculenta, sucu box)
    return item.properties && item.properties._gift === 'true';
  },

  currentTier(totalCents) {
    let reached = null;
    for (const tier of this.TIERS) {
      if (totalCents >= tier.threshold) reached = tier;
    }
    return reached;
  },

  nextTier(totalCents) {
    return this.TIERS.find(t => totalCents < t.threshold) || null;
  },

  async sync() {
    // Guardia única: cubre renderContents, el polling y cualquier llamada futura.
    if (!RewardBarSwitch.enabled) return;

    try {
      const resp = await fetch('/cart.js');
      const cart = await resp.json();

      const giftItems   = cart.items.filter(i => this.isGiftItem(i));
      const normalItems = cart.items.filter(i => !this.isGiftItem(i));
      const totalCents  = normalItems.reduce((sum, i) => sum + i.final_line_price, 0);

      this.updateUI(totalCents);

      if (!this._processing) {
        await this.manageGift(totalCents, giftItems);
      }
    } catch(e) {
      console.warn('[CartRewardBar] sync error:', e);
    }
  },

  updateUI(totalCents) {
    if (!RewardBarSwitch.enabled) return;

    const fill = document.getElementById('CartDrawer-RewardFill');
    const text = document.getElementById('CartDrawer-RewardText');
    if (!fill || !text) return;

    const maxThreshold = this.TIERS[this.TIERS.length - 1].threshold;
    const pct = Math.min((totalCents / maxThreshold) * 100, 100);
    fill.style.width = pct + '%';

    const tier = this.currentTier(totalCents);
    const next = this.nextTier(totalCents);

    if (tier && !next) {
      text.innerHTML = '🎁 ¡Agregamos 6 suculentas de regalo a tu pedido!';
      text.classList.add('is-reached');
    } else if (tier && next) {
      text.innerHTML = '🎁 ¡Regalo agregado! Compra <strong>' + this.formatMoney(next.threshold - totalCents) + '</strong> más para obtener 6 suculentas de regalo';
      text.classList.add('is-reached');
    } else {
      text.innerHTML = '🎁 Compra <strong>' + this.formatMoney(this.TIERS[0].threshold - totalCents) + '</strong> más para obtener un regalo';
      text.classList.remove('is-reached');
    }

    this.TIERS.forEach((t, i) => {
      const dot = document.getElementById('CartDrawer-RewardDot-' + i);
      if (!dot) return;
      if (totalCents >= t.threshold) dot.classList.add('is-reached');
      else dot.classList.remove('is-reached');
    });
  },

  async manageGift(totalCents, giftItems) {
    if (!RewardBarSwitch.enabled) return;

    this._processing = true;
    try {
      const tier = this.currentTier(totalCents);
      let needsRefresh = false;

      if (!tier) {
        for (const item of giftItems) {
          await this.removeItem(item.key);
          needsRefresh = true;
        }
      } else {
        const correctItems = giftItems.filter(i => i.variant_id === tier.variantId);
        const wrongItems   = giftItems.filter(i => i.variant_id !== tier.variantId);

        for (const item of wrongItems) {
          await this.removeItem(item.key);
          needsRefresh = true;
        }

        if (correctItems.length === 0) {
          const r = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: tier.variantId,
              quantity: tier.quantity,
              properties: { _gift: 'true' }
            })
          });
          if (r.ok) needsRefresh = true;

        } else if (correctItems.length > 1) {
          for (let i = 1; i < correctItems.length; i++) {
            await this.removeItem(correctItems[i].key);
            needsRefresh = true;
          }
        }
      }

      if (needsRefresh) await this.refreshDrawer();

    } catch(e) {
      console.warn('[CartRewardBar] manageGift error:', e);
    } finally {
      this._processing = false;
    }
  },

  /* FALTABA en la versión anterior: removeAllGifts() la llamaba y no existía,
     así que lanzaba TypeError y el catch silencioso se lo comía. Los regalos
     nunca se removían al apagar el kill switch. */
  removeItem(lineKey) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: 0 })
    });
  },

  async refreshDrawer() {
    try {
      const resp = await fetch('/?section_id=cart-drawer', {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!resp.ok) return;

      const html     = await resp.text();
      const parsed   = new DOMParser().parseFromString(html, 'text/html');
      const newInner = parsed.querySelector('.drawer__inner');
      const target   = document.querySelector('#CartDrawer .drawer__inner');

      if (newInner && target) {
        target.innerHTML = newInner.innerHTML;
        const overlay = document.getElementById('CartDrawer-Overlay');
        const drawer  = document.querySelector('cart-drawer');
        if (overlay && drawer) overlay.addEventListener('click', drawer.close.bind(drawer));
        const cartResp    = await fetch('/cart.js');
        const cart        = await cartResp.json();
        const normalItems = cart.items.filter(i => !this.isGiftItem(i));
        const totalCents  = normalItems.reduce((sum, i) => sum + i.final_line_price, 0);
        this.updateUI(totalCents);
        this.refreshCartIcon();
      }
    } catch(e) {
      console.warn('[CartRewardBar] refreshDrawer error:', e);
    }
  },

  async refreshCartIcon() {
    try {
      const resp = await fetch('/?section_id=cart-icon-bubble', {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!resp.ok) return;
      const html      = await resp.text();
      const parsed    = new DOMParser().parseFromString(html, 'text/html');
      const newBubble = parsed.querySelector('.shopify-section');
      const target    = document.getElementById('cart-icon-bubble');
      if (newBubble && target) target.innerHTML = newBubble.innerHTML;
    } catch(e) { /* silencioso */ }
  },

  async removeAllGifts() {
    if (this._purging) return 0; // remover dispara re-render -> evitar bucle
    this._purging = true;
    try {
      const resp = await fetch('/cart.js', { cache: 'no-store' });
      const cart = await resp.json();
      const gifts = cart.items.filter(i => this.isGiftItem(i));
      // Secuencial y por `key`: los índices se corren al borrar.
      for (const item of gifts) {
        await this.removeItem(item.key);
      }
      if (gifts.length > 0) {
        await this.refreshCartIcon();
        if (document.querySelector('#CartDrawer .drawer__inner')) {
          await this.refreshDrawer();
        }
      }
      return gifts.length;
    } catch(e) {
      console.warn('[CartRewardBar] removeAllGifts error:', e);
      return 0;
    } finally {
      this._purging = false;
    }
  },
};

/* ------------------------------------------------------------------------- *
 * 4. ARRANQUE
 * ------------------------------------------------------------------------- */
document.addEventListener('DOMContentLoaded', () => {
  RewardBarSwitch.init();
});

/* Consola, para probar sin tocar el Gist:
 *   __rewardBar.off()    apaga y limpia regalos
 *   __rewardBar.on()     prende
 *   __rewardBar.check()  vuelve a leer el Gist ahora
 *   __rewardBar.state()  estado actual
 */
window.__rewardBar = {
  on() {
    RewardBarSwitch.writeCache(true);
    RewardBarSwitch.apply(true);
    CartRewardBar.sync();
  },
  off() {
    RewardBarSwitch.writeCache(false);
    RewardBarSwitch.apply(false);
    return CartRewardBar.removeAllGifts();
  },
  async check() {
    const enabled = await RewardBarSwitch.resolve();
    RewardBarSwitch.apply(enabled);
    if (!enabled) await CartRewardBar.removeAllGifts();
    else CartRewardBar.sync();
    return enabled;
  },
  state() { return RewardBarSwitch.enabled; },
};
